import { useState, useEffect } from 'react';
import { PaymentLink, PaymentStatus } from '../types/PaymentLink';
import { subscribeToPaymentLinks } from '../services/paymentLinkService';

/**
 * Custom hook for real-time payment links data
 */
export const usePaymentLinks = (statusFilter?: PaymentStatus) => {
  const [paymentLinks, setPaymentLinks] = useState<PaymentLink[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    // Subscribe to real-time updates
    const unsubscribe = subscribeToPaymentLinks(
      (links) => {
        setPaymentLinks(links);
        setLoading(false);
      },
      statusFilter
    );

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [statusFilter]);

  return { paymentLinks, loading, error };
};

/**
 * Custom hook for statistics
 * Updated to include Total Collected and Total Outstanding metrics
 */
export const usePaymentStats = (paymentLinks: PaymentLink[]) => {
  const [stats, setStats] = useState({
    totalLinks: 0,
    activeLinks: 0,
    successfulPayments: 0,
    totalAmount: 0,
    totalCollected: 0, // Sum of all totalPaid across all links
    totalOutstanding: 0, // Sum of all remainingAmount across all links
  });

  useEffect(() => {
    // Filter to only include parent links (exclude split payment children for overall stats)
    const parentLinks = paymentLinks.filter(link => !link.splitParentId);

    const totalLinks = parentLinks.length;

    // Active links: pending, visited, processing, or partial status
    const activeLinks = parentLinks.filter(
      (link) => link.status === 'pending' || link.status === 'visited' || link.status === 'processing' || link.status === 'partial'
    ).length;

    // Successful payments: count of fully paid links
    const successfulPayments = parentLinks.filter(
      (link) => link.status === 'success'
    ).length;

    // Total amount of successful invoices
    const totalAmount = parentLinks
      .filter((link) => link.status === 'success')
      .reduce((sum, link) => sum + link.invoiceAmount, 0);

    // Total Collected: sum of all money received across ALL links
    const totalCollected = parentLinks.reduce((sum, link) => sum + (link.totalPaid || 0), 0);

    // Total Outstanding: sum of all remaining amounts (unpaid balances)
    const totalOutstanding = parentLinks.reduce((sum, link) => sum + (link.remainingAmount || 0), 0);

    setStats({
      totalLinks,
      activeLinks,
      successfulPayments,
      totalAmount,
      totalCollected,
      totalOutstanding,
    });
  }, [paymentLinks]);

  return stats;
};
